import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Award, Users, Globe, TrendingUp, Heart } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const About = () => {
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Innovation First',
      description: 'Pushing boundaries in quantum computing, semiconductors, and superconducting systems',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Collaborative Excellence',
      description: 'Building partnerships across academia, industry, and research institutions',
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Transparency',
      description: 'Open communication and ethical practices in all our endeavors',
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Impact Driven',
      description: 'Creating technologies that benefit humanity and advance scientific progress',
    },
  ];

  const timeline = [
    { year: '2020', event: 'Company founded with vision to democratize quantum computing' },
    { year: '2021', event: 'First superconducting qubit chip developed and tested' },
    { year: '2022', event: 'Partnership with leading universities for quantum research' },
    { year: '2023', event: 'Breakthrough in quantum error correction algorithms' },
    { year: '2024', event: 'Commercial launch of quantum computing platform' },
    { year: '2025', event: 'Expansion to 10+ countries, 100+ team members' },
  ];

  const team = [
    {
      role: 'Leadership',
      count: '15+',
      description: 'Years average industry experience',
    },
    {
      role: 'Researchers',
      count: '50+',
      description: 'PhD-level quantum scientists',
    },
    {
      role: 'Engineers',
      count: '40+',
      description: 'Hardware & software specialists',
    },
    {
      role: 'Global',
      count: '10+',
      description: 'Countries with presence',
    },
  ];

  const achievements = [
    '15+ patents in quantum computing',
    '150+ peer-reviewed publications',
    'Partnerships with Fortune 500 companies',
    '$100M+ in research funding',
    '25+ industry awards and recognitions',
    'ISO 27001 certified operations',
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes slide-in-left {
          from { transform: translateX(-50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out forwards;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[60vh] flex items-center ${classes.bg} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 ${theme === 'light' ? 'bg-blue-400' : theme === 'dark' ? 'bg-cyan-400' : 'bg-purple-400'} rounded-full opacity-30`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
          <div className="animate-slide-in-up">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
              Building the{' '}
              <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                Quantum Future
              </span>
            </h1>
            <p className={`text-lg sm:text-xl ${classes.subtext} max-w-3xl mx-auto`}>
              GLYQIX is pioneering the integration of quantum computing, semiconductors, and superconducting
              technologies to solve humanity's most challenging computational problems.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            <div className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} animate-slide-in-left`}>
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-6`}>
                <Target className="w-8 h-8" />
              </div>
              <h2 className={`text-2xl sm:text-3xl font-bold ${classes.text} mb-4`}>Our Mission</h2>
              <p className={`${classes.subtext} text-base sm:text-lg leading-relaxed`}>
                To make quantum computing accessible, reliable, and practical for industries worldwide.
                We're building the infrastructure that will power the next generation of scientific
                discoveries, from drug development to climate modeling.
              </p>
            </div>

            <div className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} animate-slide-in-left`} style={{ animationDelay: '0.2s' }}>
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-6`}>
                <Eye className="w-8 h-8" />
              </div>
              <h2 className={`text-2xl sm:text-3xl font-bold ${classes.text} mb-4`}>Our Vision</h2>
              <p className={`${classes.subtext} text-base sm:text-lg leading-relaxed`}>
                A world where quantum and superconducting technologies are seamlessly integrated into
                everyday computing infrastructure, enabling breakthroughs in healthcare, finance,
                cybersecurity, and artificial intelligence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Our Core Values
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} text-center animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                  {value.icon}
                </div>
                <h3 className={`text-xl font-bold ${classes.text} mb-3`}>{value.title}</h3>
                <p className={`${classes.subtext} text-sm`}>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Our Journey
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              Milestones in quantum innovation
            </p>
          </div>

          <div className="relative">
            <div className={`absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b ${classes.accent}`} />
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className="relative pl-12 sm:pl-20 animate-slide-in-left"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`absolute left-2 sm:left-5 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r ${classes.accent} border-4 ${theme === 'light' ? 'border-white' : theme === 'dark' ? 'border-slate-900' : 'border-slate-950'}`} />
                  <div className={`${classes.cardBg} rounded-xl p-4 sm:p-6 border ${classes.cardBorder}`}>
                    <div className={`text-xl sm:text-2xl font-bold ${classes.accentText} mb-2`}>{item.year}</div>
                    <p className={`${classes.text} text-sm sm:text-base`}>{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Stats */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              World-Class Team
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {team.map((stat, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} text-center animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`text-3xl sm:text-4xl font-bold ${classes.accentText} mb-2`}>{stat.count}</div>
                <div className={`text-base sm:text-lg font-semibold ${classes.text} mb-1`}>{stat.role}</div>
                <p className={`text-xs sm:text-sm ${classes.subtext}`}>{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Key Achievements
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 ${classes.cardBg} backdrop-blur-md rounded-xl p-4 border ${classes.cardBorder} animate-slide-in-up`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Award className={`w-6 h-6 ${classes.accentText} flex-shrink-0`} />
                <span className={`${classes.text} text-sm sm:text-base`}>{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Join Our Mission
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            Be part of the team building the future of quantum computing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/careers"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
            >
              View Careers
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                theme === 'light' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' :
                theme === 'dark' ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950' :
                'border-purple-500 text-purple-400 hover:bg-purple-950'
              } transition-all`}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;